import { type CSSProperties, type ReactNode, useRef } from 'react';
import type { TreeNodeData } from '../models';
import styles from './styles.module.scss';
import { useNodeDataMap } from './NodeDataContext';
import { NodeLink } from './NodeLink';
import { getSkillNodeDirection, Spread } from '../util';

export const TreeNode = ({
  skill,
  direction,
  children,
  link,
  isRoot,
}: {
  skill: TreeNodeData;
  direction: number;
  link: ({ skill }: { skill: TreeNodeData }) => ReactNode;
  children: ({ skill }: { skill: TreeNodeData }) => ReactNode;
  isRoot?: boolean;
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  const itemsMap = useNodeDataMap();

  return (
    <div
      ref={nodeRef}
      tabIndex={1}
      style={
        {
          '--length-multiplier': skill.nodeConfig?.lengthMultiplier || 1,
        } as CSSProperties
      }
      className={`${styles.treeNode} ${isRoot ? styles.root : styles['position_' + direction]}`}
    >
      {!isRoot && !skill.nodeConfig?.hideLink && <NodeLink nodeRef={nodeRef}>{link({ skill })}</NodeLink>}

      <div className={styles.nodeContainer}>
        {children({ skill })}

        {skill.children.map((skillId: string, index: number) => {
          const childSkill = itemsMap.get(skillId);
          if (!childSkill) {
            console.error(`Skill ${skillId} not found`);
            return null;
          }

          return (
            <TreeNode
              key={skillId}
              skill={childSkill}
              link={link}
              direction={getSkillNodeDirection(index, {
                overridePlacement: skill.subTreeConfig?.overridePlacement,
                offset: skill.subTreeConfig?.direction ?? direction + (skill.subTreeConfig?.rotateBy || 0),
                spread: isRoot ? Spread.Opposing : Spread.Tree,
              })}
            >
              {children}
            </TreeNode>
          );
        })}
      </div>
    </div>
  );
};
